import React, { useState, useEffect, createContext, useContext, ReactNode, FC } from 'react';

export type Language = 'en' | 'fr';

const translations = {
  en: {
    common: {
      save: "Save",
      cancel: "Cancel",
      delete: "Delete",
      update: "Update",
      loading: "Loading...",
      success: "Success",
      error: "Error",
      confirm: "Confirm",
      back: "Back",
      edit: "Edit",
      done: "Done",
      status: "Status",
      type: "Type",
      date: "Date",
      time: "Time",
      day: "Day",
      week: "Week",
      month: "Month",
      quarter: "Quarter",
      today: "Today",
      duration: "Duration",
      notes: "To-dos",
      credits: "Credits",
      lessons: "Lessons",
      student: "Student",
      teacher: "Teacher",
      scheduled: "Scheduled",
      completed: "Completed",
      canceled: "Canceled",
      pending: "Pending",
      due: "Due",
      left: "Left",
      add: "Add",
      remove: "Remove",
      primary: "Primary",
      failed: "Failed",
      active: "Active",
      archived: "Archived",
      dropped: "Gone",
      paymentDue: "Payment Due",
      actions: "Actions",
      assignColor: "Assign a Color",
      filter: "Filter",
      sort: "Sort",
      newest: "Newest",
      oldest: "Oldest",
      page: "Page",
      anonymous: "Anonymous User",
      expandAll: "Expand All",
      collapseAll: "Collapse All",
      copy: "Copy",
      copied: "Copied!",
      total: "Total",
      publish: "Publish",
      unpublish: "Unpublish",
      published: "Published",
      draft: "Draft",
      location: "Location",
      remote: "Remote Available",
      preview: "Preview",
      contact: "Contact",
      book: "Book a Lesson",
      backToEditor: "Back to Editor",
      draftMode: "Draft Mode",
      liveProfile: "Live Profile",
      editProfile: "Edit Profile",
      draftPreview: "Draft Preview"
    },
    nav: {
      dashboard: "Dashboard",
      students: "Students",
      planner: "Planner",
      insights: "Insights",
      profile: "Account Settings",
      support: "Support & Feedback",
      guide: "User Guide",
      programs: "Curriculum",
      signOut: "Sign Out",
      studioHub: "Class Hub",
      switchLanguage: "Switch Language",
      toggleTheme: "Toggle Theme",
      openMenu: "Open Menu",
      workflow: "Workflow",
      pricing: "Pricing",
      marketplace: "Find a Teacher",
      publicProfile: "Public Presence",
      reviews: "Feedback"
    },
    reviews: {
      title: "Student Feedback",
      desc: "Monitor your reputation and read what your students have to say.",
      avgRating: "Average Rating",
      totalReviews: "Total Reviews",
      noReviews: "No feedback received from students yet.",
      anonymousDesc: "For privacy, student photos are hidden and only first names are displayed.",
      ratingLabel: "{count} Stars",
      filterAll: "All Ratings",
      sortByNewest: "Newest First",
      sortByOldest: "Oldest First",
      sortByHighest: "Highest Rating",
      sortByLowest: "Lowest Rating",
      reputationShield: "Reputation Shield",
      rateMentor: "Rate your Mentor",
      shareExperience: "Share your learning experience",
      reviewComment: "Review Comment",
      reviewPlaceholder: "Tell others what you love about this teacher...",
      submitFeedback: "Submit Feedback",
      updateFeedback: "Update Feedback",
      feedbackTitle: "Student Feedback",
      feedbackEmpty: "No public reviews yet. Be the first student to leave feedback!"
    },
    marketplace: {
      title: "Perfect music mentors",
      subtitle: "Find professional educators to accelerate your journey.",
      filters: "Refine Search",
      instrument: "Select Instrument",
      priceRange: "Price Range",
      remoteOnly: "Remote Lessons Only",
      viewProfile: "View Profile",
      noTeachers: "No teachers found matching your criteria.",
      startingFrom: "Starting from",
      perHour: "/ hour",
      teachingProfessionallyTitle: "Teaching professionally?",
      teachingProfessionallyDesc: "Publish your profile and grow your studio with SharpNote.",
      nearMe: "Near Me",
      distance: "Within {distance} km",
      allDistances: "Any distance",
      locationError: "Could not retrieve your location.",
      km: "km",
      resetLocation: "Reset Location",
      allCategories: "All Categories",
      searchPlaceholder: "Search by name, expertise, or style..."
    },
    publicProfile: {
      title: "Public Presence",
      desc: "Customize your profile to attract new students on the SharpNote marketplace.",
      basicInfo: "Basic Info",
      tagline: "Short Catchy Tagline",
      taglinePlaceholder: "e.g. Jazz Piano Virtuoso with 15 years experience",
      bio: "Biography",
      bioPlaceholder: "Tell the world about your teaching style and history...",
      curriculum: "Teaching Focus",
      instruments: "Instruments Taught",
      media: "Media & Gallery",
      galleryDesc: "Add up to 5 image URLs of you performing or teaching.",
      videoUrl: "YouTube Video ID",
      pricing: "Rates & Packages",
      hourlyRate: "Hourly Rate",
      addPack: "Add Discount Pack",
      packLabel: "Pack Name (e.g. 10 Lessons)",
      packPrice: "Pack Total Price",
      visibility: "Profile Visibility",
      publishDesc: "Make your profile discoverable to the public.",
      unpublishDesc: "Hide your profile while you're still editing.",
      mustSignIn: "Sign up to contact this teacher.",
      preciseLocation: "Precise Location",
      preciseLocationDesc: "(For Distance Search)",
      useCurrentLocation: "Use My Current Location",
      locationFound: "Coordinates set!",
      certifiedEducator: "Certified SharpNote Educator",
      safeVerified: "Safe & Verified",
      safeVerifiedDesc: "Transactions are managed directly through SharpNote's prepaid system once connected to a class.",
      stageName: "Stage Name",
      city: "City",
      country: "Country",
      lessonPackages: "Lesson Packages",
      optional: "Optional",
      savingsPercent: "Savings%",
      profileRequirements: "Profiles must have all required fields and a bio (50 chars min) to be meaningful.",
      publishChanges: "Publish Changes"
    },
    tour: {
      next: "Next",
      prev: "Back",
      finish: "Got it!",
      skip: "Skip Tour",
      relaunch: "Take the Product Tour",
      relaunchDesc: "New to SharpNote? Let us show you around the orchestration platform.",
      steps: {
        welcome: {
          title: "Welcome to SharpNote!",
          content: "You're now looking at your professional studio command center. Let's take a quick look at the essentials."
        },
        schedule: {
          title: "Upcoming Schedule",
          content: "Your next performances appear here. You can join Google Meet calls, check homework, and open student portals in one click."
        },
        learners: {
          title: "Upcoming Learners",
          content: "This dynamic list shows students who have a lesson booked soon. No upcoming lesson? They stay in your full Directory instead."
        },
        students: {
          title: "Student Directory",
          content: "Manage your full roster here. Onboard new students with magic links and track their lesson credits with precision."
        },
        planner: {
          title: "Intelligent Planner",
          content: "Use the Planner to sync with Google Calendar, manage your availability, and drag-and-drop your sessions freely."
        },
        curriculum: {
          title: "Curriculum Library",
          content: "Organize your scores, backing tracks, and documents into groups. Bulk-apply resources to any lesson with one click."
        },
        insights: {
          title: "Class Insights",
          content: "Monitor your studio's health. Track weekly velocity, roster growth, and financial performance with high-fidelity charts."
        },
        reviews: {
          title: "Student Feedback",
          content: "Manage and read reviews from your students. Use this feedback to refine your teaching style and showcase your reputation."
        },
        publicPresence: {
          title: "Public Presence",
          content: "Create a professional public profile that prospective students can browse on the SharpNote marketplace. Showcase your expertise and grow your studio."
        },
        settings: {
          title: "Studio Settings",
          content: "Finalize your orchestration by connecting your Google account for Calendar sync and Drive file management."
        }
      }
    },
    pricing: {
      title: "Simple, Transparent Pricing",
      subtitle: "The orchestration platform built for the professional educator.",
      forTeachers: "For Educators",
      forStudents: "For Students",
      studentPricingTitle: "Free Forever",
      studentPricingDesc: "SharpNote is 100% free for students. You only pay your teacher directly for your lessons.",
      studentPricingPoints: [
        "Access your lesson history anytime",
        "View and download homework assets",
        "Join remote calls in one click",
        "Track your lesson credits transparently"
      ],
      testPeriodTitle: "Community Test Period",
      testPeriodDesc: "SharpNote is currently in early access. All features, including Pro tier capabilities, are currently free for all registered educators during our testing phase.",
      freeTier: "Free",
      proTier: "Pro",
      perMonth: "/ month",
      getStarted: "Get Started",
      upgradeToPro: "Upgrade to Pro",
      recommended: "Recommended",
      footerTitle: "Every tool you need to thrive",
      footerSubtitle: "No complexity, just pure orchestration.",
      directPaymentsTitle: "Direct Payments",
      directPaymentsDesc: "SharpNote is a studio management tool, not a payment processor. Students pay their teachers through their own preferred methods (Cash, Transfer, Stripe, etc.). The pricing listed above for teachers covers the cost of platform orchestration and integrations.",
      features: {
        googleCalendar: "Google Calendar Two-Way Sync",
        googleDrive: "Google Drive Asset Management",
        creditSystem: "Prepaid Credit System",
        homework: "Automated Homework & Carry-over",
        curriculum: "Modular Curriculum Library",
        studentPortal: "Dedicated Student Learning Portal",
        meet: "One-Click Google Meet Generation",
        multiDevice: "Mobile & Desktop Optimized",
        analytics: "Advanced Studio Analytics",
        studentLimit: "Up to 5 Students",
        unlimitedStudents: "Unlimited Students",
      },
      grid: {
        hub: { title: "Class Hub", desc: "A central mission control for your studio, students, and curriculum." },
        roster: { title: "Roster Management", desc: "Easily onboard new students with magic links and manage histories." },
        automation: { title: "Automation", desc: "Notes and files automatically carry over between your lessons." },
        curriculum: { title: "Studio Brain", desc: "Organize teaching materials into modular groups for bulk planning." },
        financial: { title: "Financial Harmony", desc: "Transparent prepaid credit tracking for teachers and students." },
        growth: { title: "Growth Intelligence", desc: "Monitor weekly velocity and active roster trends with precision." },
        privacy: { title: "Data Sovereignty", desc: "We bridge your Google Workspace tools without selling your data." },
        more: { title: "And much more...", desc: "Google Meet integration, ICS exports, resource libraries, and beyond." }
      }
    },
    privacy: {
      title: "Privacy Policy",
      lastUpdated: "Last updated: February 2026",
      overview: {
        title: "1. Overview",
        content: "SharpNote is designed to be a \"Frictionless Educator\" tool. We prioritize your privacy and data sovereignty. We do not sell your personal data. We only collect information essential for orchestrating your studio and integrating with your chosen Google services."
      },
      googleData: {
        title: "2. Google Data Usage",
        desc: "SharpNote integrates with Google Calendar and Google Drive to sync lessons and manage pedagogical assets.",
        points: [
          "Google Calendar: We read and write events to sync your lesson schedule. We only access the specific calendar you authorize.",
          "Google Drive: We link files for student homework. We grant 'Reader' permissions to students you explicitly invite.",
          "Google Identity: We use Google for secure authentication."
        ]
      },
      dataSecurity: {
        title: "3. Data Security",
        content: "Your data is stored securely using Firebase (Google Cloud Platform). Communication between your browser and our servers is encrypted using SSL/TLS. We leverage Google's security architecture for authentication, ensuring your passwords are never stored directly on our servers."
      },
      studentTeacher: {
        title: "4. Student & Teacher Relationship",
        content: "Information shared between a teacher and student (notes, files, history) is restricted to those two parties. Teachers can manage student access to files and archive data as needed for studio management."
      },
      footerLink: "Have questions about your data? Contact Support"
    },
    terms: {
      title: "Terms of Service",
      lastUpdated: "Last updated: February 2026",
      acceptance: {
        title: "1. Acceptance of Terms",
        content: "By accessing or using SharpNote, you agree to be bound by these terms. SharpNote is a platform for independent music educators to manage their professional studio."
      },
      usage: {
        title: "2. Use of the Service",
        desc: "You are responsible for maintaining the confidentiality of your account.",
        points: [
          "Teachers are responsible for the content shared with students.",
          "The credit system is a management tool; SharpNote does not process payments directly.",
          "SharpNote is not responsible for any disputes between teachers and students regarding payments or lesson delivery."
        ]
      },
      liability: {
        title: "3. Limitation of Liability",
        content: "SharpNote is provided \"as is\". We are not liable for any service interruptions, data loss from connected Google services, or administrative errors made by users within the system."
      },
      changes: {
        title: "4. Changes to Service",
        content: "We reserve the right to modify or discontinue parts of the service as we evolve the platform for educators."
      }
    },
    guide: {
      title: "Studio Orchestration Manual",
      desc: "Comprehensive documentation for mastering your professional teaching environment on SharpNote.",
      toc: "Table of Contents",
      proTip: "Pro Tip",
      sections: {
        intro: {
          title: "The SharpNote Philosophy",
          content: "SharpNote is built on the 'Frictionless Educator' principle. Our goal is to reduce the cognitive load of administrative tasks—billing, scheduling, and file management—leaving you 100% focused on the pedagogical experience. The system is designed as a 'Source of Truth' that bridges your personal tools (Google Calendar, Drive) with your students' learning portal."
        },
        onboarding: {
          title: "Onboarding & Invitations",
          content: "To add a student, navigate to the 'Students' directory and click 'Invite Student'. Enter their email. If the email is already registered, a connection is made instantly. If not, they are marked as 'Pending'. Once the student signs up using that specific email, SharpNote automatically merges the invitation and populates their historical lessons and credits.",
          tip: "Students don't need a separate signup link—just tell them to use the same email you invited."
        },
        credits: {
          title: "Credit System & Billing",
          content: "SharpNote uses a prepaid 'Credits' model. Each lesson costs 1 credit. When you mark a lesson as 'Completed', the student's balance is automatically decremented. In your Profile settings, define 'Credit Packs' (e.g., 5-lesson bundle). When a student pays you (via your external billing method), log the purchase on their profile in SharpNote to update their balance and track revenue.",
          tip: "Use the 'Payment Due' filter in the directory to quickly see who needs to top up."
        },
        planner: {
          title: "Intelligent Planner",
          content: "The Planner is a two-way sync interface with Google Calendar. Drag lessons to reschedule them; the changes push to Google instantly. Click and drag on empty slots to create new performances. Use the 'Block Time' feature to protect your personal hours, vacations, or admin time without affecting student credits.",
          tip: "Check the 'Availability View' inside the schedule modal to detect overlaps with existing sessions."
        },
        lessons: {
          title: "The Performance Workflow",
          content: "Every lesson is a 'Performance'. You can add to-dos (homework instructions) and link files directly from your Google Drive. SharpNote automatically generates a Google Meet link if enabled. Upon completion, use the 'Carry Over' feature to move unfinished to-dos or specific files to the next scheduled session, ensuring continuity in learning.",
          tip: "Click the 'Join Meet' button directly from your Dashboard to jump into a call."
        },
        churn: {
          title: "Managing Student Churn",
          content: "When a student finishes their time with your studio, don't delete them immediately. Use the 'Flag as Gone' action in their profile. This removes them from your active list and revokes their Google Drive file access while preserving their lesson history and revenue data for your stats.",
          tip: "You can 'Restore' a student at any time if they decide to come back, which automatically regrants Drive permissions."
        },
        curriculum: {
          title: "Curriculum Library",
          content: "The Curriculum section is your modular teaching brain. Organize backing tracks, scores, and documents into groups (e.g., 'Beginner Jazz' or 'Scale Exercises'). When scheduling a lesson, use the 'Apply Curriculum' button to bulk-import every file from a group into that session with one click.",
          tip: "You can drag and drop groups and files to reorganize your curriculum structure freely."
        },
        stats: {
          title: "Data & Insights",
          content: "Use the 'Insights' tab to monitor your 'Weekly Velocity' (average sessions per week) and 'Active Roster' growth. Set professional targets in the 'Adjust Goals' menu. Drill down into specific years or months on the charts to identify seasonal trends in your teaching volume.",
          tip: "Filtered stats show you student-specific 'Attendance Rates' and credit usage efficiency."
        }
      }
    },
    programs: {
      title: "Curriculum Library",
      desc: "Organize your teaching materials into modular groups for quick lesson planning.",
      newGroup: "New Group",
      editGroup: "Edit Group",
      deleteGroup: "Delete Group",
      groupName: "Group Name",
      groupDesc: "Description",
      addFile: "Add File",
      noGroups: "No curriculum groups created yet.",
      selectGroup: "Select Group",
      bulkAdd: "Apply Curriculum",
      applyTooltip: "Import all files from this curriculum group to the current lesson.",
      moveToRoot: "Move to Top Level",
      confirmDeleteTitle: "Delete Group?",
      confirmDeleteDesc: "Are you sure you want to delete '{name}'? This will also unnest all sub-groups. This action cannot be undone.",
      selection: "Selection",
      assetsSelected: "{count} Assets Selected",
      searchPlaceholder: "Search curriculum groups and files...",
      quickAddTooltip: "Quick Add Assets",
      addSubTooltip: "Add Sub-group",
      invalidMove: "Action invalid: A group cannot be moved inside one of its own descendants.",
      movingFile: "Moving File",
      movingGroup: "Moving Group",
      assetsTitle: "Curriculum Assets",
      noAssets: "No assets linked to this group yet.",
      assetsCount: "{count} assets",
    },
    app: {
      verifyEmail: "Verify your email",
      linkSent: "Verification link sent to",
      linkResent: "Link Resent!",
      resendLink: "Resend Verification Email",
      verifiedAction: "I've verified my email",
      accessPending: "Access Pending",
      accessPendingDesc: "Your account is created but not yet assigned to a class.",
      teacherContact: "Teachers: Contact",
      studentContact: "Students: Ask your teacher to invite you using your registered email.",
      checkAccess: "Check for Invitations",
    },
    dashboard: {
      hi: "Hi",
      performanceFeed: "Upcoming lessons",
      remainingSessions: "You have {count} sessions planned.",
      newPerformance: "New Lesson",
      upcomingSchedule: "Upcoming Schedule",
      noSessions: "No lesson currently booked.",
      noSessionsAvailable: "No details available.",
      noConnections: "No active connections found.",
      activeRoster: "Active Students",
      upcomingLearners: "Upcoming Learners",
      upcomingMentors: "Your Mentors",
      studioMentors: "Classes Mentors",
      performanceNotes: "Lesson To-dos",
      linkedResources: "Linked Resources",
      openPortal: "Open Class Portal",
      detailsPending: "No to-dos available.",
      joinMeet: "Join Google Meet",
    },
    students: {
      directory: "Student Directory",
      manageRoster: "Manage your student list and pending invitations.",
      inviteStudent: "Invite Student",
      searchPlaceholder: "Search students by name or email...",
      noStudents: "No students found matching your criteria.",
      updatingRoster: "Updating Student List",
      inviteFailed: "Failed to process invitation.",
      showArchived: "Show Archived",
      showActive: "Show Active",
      cardView: "Card View",
      listView: "List View",
      sorting: {
        nameAZ: "Name (A-Z)",
        nameZA: "Name (Z-A)",
        creditsLow: "Credits (Low-High)",
        creditsHigh: "Credits (High-Low)",
        dateNewest: "Joined (Newest)",
        dateOldest: "Joined (Oldest)",
      },
      filtering: {
        all: "All Balances",
        outOfCredits: "Out of Credits",
        lowCredits: "Low Credits (1-2)",
        healthyBalance: "Healthy Balance (3+)",
      },
      inviteModal: {
        title: "Invite Student",
        desc: "Enter their email to create a connection. If they don't have an account yet, they'll join your studio automatically after signing up.",
        emailLabel: "Email Address",
        emailPlaceholder: "virtuoso@example.com",
        submit: "Process Invitation",
        successTitle: "Invitation Sent",
        successDesc: "The student will be automatically added when they join.",
        duplicateError: "This student is already in your directory.",
        magicLinkTitle: "Magic Link",
        magicLinkDesc: "Copy and share this link with your student to help them sign up.",
      },
    },
    planner: {
      title: "Lesson Planner",
      desc: "Coordinate your lessons schedule and availability.",
      export: "Export ICS",
      sync: "Sync",
      syncAction: "Sync Calendar",
      syncing: "Syncing...",
      newSession: "New Lesson",
      blockTimeAction: "Block Time",
      tips: {
        title: "Interactive Planner Tips",
        desc: "Drag and drop lessons to reschedule. Click any lesson to directly edit its time, student, or to-dos.",
      },
      legend: {
        lesson: "Lesson",
        blocked: "Blocked",
      },
      syncWarning: {
        notConfigured: "Google Calendar sync is not configured. Lessons will only exist in SharpNote.",
        expired: "Google Calendar connection expired. Re-authenticate in settings to resume sync.",
        configureAction: "Configure Sync",
        reconnectAction: "Reconnect",
      },
      errors: {
        dbConnection: "Connection to Studio database lost.",
        syncError: "Conflict detected or connection lost. Reschedule reverted.",
      },
      engine: "Let's organize!",
    },
    stats: {
      engine: "Let's organize!",
      title: "Class Insights",
      desc: "Deep dive into your professional growth and operation efficiency.",
      activeRoster: "Active Students",
      churnedStudents: "Churned Students",
      completionRate: "Completion rate",
      creditsSold: "Credits Sold",
      historical: "Historical",
      healthy: "Healthy",
      target: "Target: 95%",
      billed: "Billed",
      volumeTitle: "Performance Volume",
      volumeDesc: "Lesson density grouped by selected interval.",
      completedSessions: "Completed Sessions",
      lifecycleTitle: "Credit Lifecycle",
      lifecycleDesc: "Billed vs. Utilized ratio.",
      utilized: "Utilized",
      remaining: "Remaining",
      utility: "Utility",
      totalPotential: "Total Potential",
      activeLiability: "Active Liability",
      units: "Units",
      efficiency: "Operational Efficiency",
      weeklyVelocity: "Weekly velocity",
      avgSessions: "Average sessions / week",
      retention: "Session Retention",
      vsNoShow: "Completed vs. No-show",
      benchmark: "Benchmark: 85%",
      totalChurn: "Total Churn",
      vsActive: "Gone vs. Active",
      pipeline: "Growth Engine",
      growthForecast: "Goals Progression",
      required: "Students Remaining",
      capacityTip: "Recommended studio capacity based on your goals.",
      tenure: "Avg. Engagement",
      duration: "Avg. Duration",
      suggestion: "Suggestion",
      suggestionText: "Churn tracking shows {count} students have moved on. Use these insights to refine your curriculum or referral strategy.",
      harmonizing: "Harmonizing Studio Data",
      goalsTitle: "Professional Targets",
      setGoals: "Adjust Goals",
      targetRoster: "Active Students Goal",
      targetVolume: "Weekly Lesson Goal",
      progression: "Progression",
      avgSessionsPerStudent: "Sessions / Student",
      allStudents: "All Students",
      selectStudent: "Filter by Student",
      viewingStatsFor: "Viewing stats for",
      attendanceRate: "Attendance Rate",
      learningFrequency: "Learning Frequency",
      studentPerformance: "Student Performance",
      creditUtilization: "Credit Utilization",
      momentum: "Weekly Momentum",
      momentumTip: "Sessions per week average for this student.",
      utilizationTip: "Usage of current prepaid credit package.",
      insightLowCredits: "{name} is running low on credits ({count} left). Consider requesting a top-up.",
      insightConsistent: "{name} has a very consistent learning momentum. Excellent progress!",
      insightIrregular: "{name}'s schedule has been irregular lately. Check in to ensure engagement.",
      insightPerfectAttendance: "Perfect attendance record for {name}! Keep the motivation high.",
      estimatedEarnings: "Estimated Earnings",
      allTime: "All Time",
      revenueBreakdown: "Revenue Breakdown",
      noPurchases: "No purchases recorded yet.",
      totalEstimated: "Total Estimated",
      annualVolume: "Annual Lesson Volume",
      monthlyBreakdown: "{year} Breakdown",
      clickYearTip: "Click a year to view monthly data.",
      showingMonthly: "Showing lessons per month.",
      totalLessonsBought: "Total Lessons Bought",
      creditsAvailable: "Credits Available",
      performanceQuality: "Performance Quality",
      lessonsLast30Days: "Lessons in Last 30 Days",
      engagementRate: "Engagement Rate",
      enrollmentStatus: "Enrollment Status",
      noRecommendations: "No urgent recommendations for {name} at this time.",
      studentHistory: "{name}'s Lesson History",
      studentYearly: "{name}'s {year} Breakdown",
      yearlyBreakdown: "{year} Breakdown",
      noData: "NO DATA",
      activateInsights: "Complete your first lesson to activate these insights.",
      trackLiability: "Define credit packs and log purchases to track studio liability.",
      overallRating: "Overall Rating",
      noRatings: "No Ratings",
      reviewsCount: "reviews",
      lessonsDone: "done",
    },
    scheduleModal: {
      performance: "Performance",
      blockTime: "Block Time",
      editPerformance: "Edit Performance",
      editBlock: "Edit Blocked Time",
      scheduleSession: "Schedule A Lesson",
      blockAvailability: "Block Availability",
      editDesc: "Modify the details of your existing lesson.",
      scheduleDesc: "Initialize a lesson slot in your student roster.",
      blockDesc: "Mark hours as unavailable for vacations, breaks, or admin.",
      syncExpired: "Sync Expired",
      syncExpiredDesc: "Lesson invalid. Logging out...",
      confirmRemoval: "Confirm Removal",
      removalDesc: "This will permanently delete the entry from SharpNote and your Google Calendar. This cannot be undone.",
      deletePermanently: "Delete Permanently",
      keepSession: "Keep Lesson",
      assignedStudent: "Assigned Student",
      selectStudent: "Select Student...",
      creditsRemaining: "{count} Credits Remaining",
      blockTitle: "Block Title",
      blockPlaceholder: "e.g. Lunch Break, Personal...",
      dateTime: "Date & Time",
      durationStrategy: "Duration",
      sessionNotes: "Lesson To-dos",
      notesPlaceholder: "Add a goal or lesson point...",
      assetLibrary: "Asset Library",
      linkDrive: "Link Drive File",
      confirmPerformance: "Confirm Performance",
      blockStudio: "Block Studio Hours",
      updateSession: "Update Lesson",
      authRequired: "Cloud connection required. Please verify Google Drive permissions in settings.",
      deploymentFailed: "Deployment failed. Studio bridge unstable.",
      addNote: "Add To-do",
      noStudentsWarning: "You need to add a student to your list before scheduling a lesson.",
      goToStudents: "Go to Students Directory",
      availabilityView: "Availability View",
      availabilitySub: "Select a slot or view conflicts",
      conflictDetected: "Schedule Conflict Detected",
      conflictWith: "This slot overlaps with: {name}",
      ignoreConflict: "Ignore Conflict & Confirm",
      calendarTips: "Use arrows to navigate periods. In Month view, click a day to zoom in. Click and drag to pick a specific performance time.",
      conferencing: "Conferencing",
      createMeet: "Create Google Meet",
    },
    studentDetail: {
      backToDashboard: "Back to Dashboard",
      gone: "Gone",
      enrollmentDate: "Enrollment Date",
      joinedOn: "Joined {date}",
      studioBalance: "Lessons Credits",
      available: "Available",
      left: "Left",
      completed: "Completed",
      sessionRecorded: "Lesson Recorded!",
      rebookDesc: "The credits have been updated. Ready to book the next lesson with these instructions?",
      rebookAction: "Re-book Next Lesson",
      upcomingPerformance: "Upcoming Performance",
      noUpcoming: "No upcoming lessons are currently scheduled.",
      archivedHistory: "Archived History",
      noHistory: "No archived lesson history available.",
      planInstructions: "Lesson Plan & To-dos",
      noInstructions: "No specific to-dos recorded.",
      attachedResources: "Attached Resources",
      noAttachments: "No Attachments",
      flagAsGone: "Flag as Gone",
      restoreStudent: "Restore Student",
      paidTotal: "Shared Library",
      sharedFiles: "Shared Library",
      browseFiles: "Browse Files",
      filesTitle: "Resource Library",
      filesDesc: "Search and filter all documents shared during your lessons.",
      searchFiles: "Search files...",
      noFilesFound: "No files found matching your filters.",
      allTypes: "All Formats",
      purchasePack: "Purchase Pack",
      logPurchase: "Log a new credit pack purchase for {name}.",
      noPacksConfigured: "No packs configured.",
      goToSettings: "Go to Settings",
      confirmPurchase: "Confirm Purchase",
      closeRecord: "Close Record",
      viewProfile: "View Student Profile",
      creditHistory: "Credit Evolution History",
      creditHistoryDesc: "Track all credit balance changes over time.",
      noCreditHistory: "No history found for this student.",
      changeAmount: "Change",
      balance: "Balance",
      typeManual: "Manual Update",
      typePack: "Pack Purchase",
      typeLesson: "Lesson Completion",
      typeSync: "Calendar Sync",
      modals: {
        cancelTitle: "Cancel Lesson?",
        cancelDesc: "This will permanently remove the lesson from the planner. This action cannot be reversed.",
        flagGoneTitle: "Flag Student as Gone?",
        flagGoneDesc: "This will remove {name} from your active student list. Their historical data will be preserved.",
        restoreTitle: "Restore Student?",
        restoreDesc: "This will return {name} to your active roster.",
        deleteTitle: "Permanent Delete?",
        deleteDesc: "You are about to permanently remove {name} and all associated data. Irreversible.",
        earlyTitle: "Early Completion?",
        earlyDesc: "This lesson is scheduled for {date}. Marking it done now will deduct 1 credit. Intentional?",
        completeTitle: "Lesson Completed!",
        completeDesc: "1 credit was spent. {count} credits left for this student.",
        forwardNotes: "Select to-dos to carry over",
        forwardFiles: "Select files to carry over",
        loggedTitle: "Performance Logged!",
        loggedDesc: "Credits have been updated. Would you like to schedule the next lesson for {name} now?",
        bookNext: "Plan next session",
        doneForNow: "Don't plan yet",
        confirmCompleteTitle: "Complete Lesson?",
        confirmCompleteDesc: "Are you sure you want to mark this lesson as completed? 1 credit will be deducted.",
      }
    },
    profile: {
      title: "Account Settings",
      desc: "Manage your professional profile and Google integrations.",
      personalInfo: "Professional Access",
      fullName: "Full Name",
      firstName: "First Name",
      lastName: "Last Name",
      phoneNumber: "Phone Number",
      loginEmail: "Login Email",
      googleSyncTitle: "Google Calendar Sync",
      googleSyncDesc: "Automatically push lessons to your preferred Google Calendar.",
      googleImportTitle: "Enable Import Sync",
      googleImportDesc: "Automatically import lessons from your Google Calendar based on student emails.",
      connectionExpired: "Connection Expired",
      expiredDesc: "Your Google session has ended. Re-authenticate to continue syncing.",
      reconnect: "Reconnect Google Account",
      targetCalendar: "Target Calendar",
      syncDisabled: "Sync Disabled",
      syncEnabled: "Calendar Sync Enabled",
      secureConnectionTeacher: "Only your students can see your name, email and phone number.",
      secureConnectionStudent: "Only your teachers can see your name, email and phone number.",
      updateProfile: "Update Profile",
      updateFailed: "Update failed",
      currency: "Currency",
      creditPacksTitle: "Credit Packs",
      creditPacksDesc: "Define packages your students can purchase in {currency}.",
      packName: "Pack Name",
      price: "Price",
      noPacks: "No packs configured.",
      automationTitle: "Studio Automation",
      automationDesc: "Configure default behaviors for session creation.",
      defaultMeet: "Default Meet",
      defaultMeetDesc: "Automatically add a Google Meet link to new sessions.",
      notifications: "Notifications",
      notificationsDesc: "Alert students and teachers before the performance starts.",
      minutesBefore: "minutes before",
    },
    support: {
      title: "Support & Feedback",
      desc: "Need help or have an idea to improve SharpNote? We're here to listen.",
      reportIssue: "Report an Issue",
      reportIssueDesc: "Found a bug or something isn't working as expected? Let us know.",
      makeSuggestion: "Make a Suggestion",
      makeSuggestionDesc: "Have an idea for a new feature? We'd love to hear your thoughts.",
      contactAdmin: "Contact Administrator",
      emailUs: "Email Support"
    },
    login: {
      signIn: "Sign In",
      register: "Register",
      email: "Email",
      password: "Password",
      googleSync: "Connect with Google",
      authFailed: "Authentication failed. Check your credentials.",
      createAccount: "Create Account",
      authenticate: "Authenticate",
      socialIntegration: "Social Login",
      home: "Home",
      tagline: "Professional Student Management",
    },
    landing: {
      tagline: "The Platform for Independent Music Educators & Students",
      heroTitle1: "Music Teaching &",
      heroTitle2: "Learning Evolved.",
      heroDesc: "For Teachers: Orchestrate your professional studio. For Students: Discover world-class mentors and track your progress in one dedicated portal.",
      heroSlogan: "Look sharp, not flat. The orchestration platform for everyone.",
      ctaStart: "For Teachers",
      ctaMarketplace: "Find a Teacher",
      ctaWatch: "Watch Workflow",
      featuresTitle: "Built for Performance",
      featuresDesc: "Meticulously crafted to bridge the gap between world-class teaching and dedicated learning.",
      trustTitle1: "Your Journey,",
      trustTitle2: "Unplugged.",
      efficiency: "Efficiency gain",
      latePayments: "Late Payments",
      readyToOrchestrate: "Ready to start your journey?",
      readyDesc: "Join the educators and students moving away from friction and back to music.",
      launch: "Launch SharpNote",
      precisionScheduling: "Precision Scheduling",
      schedulingDesc: "Live Google Calendar two-way sync. Adjust your slots and students are notified instantly in their own portal.",
      inviteMagic: "Connect with a magic link",
      organizedHistory: "Beautifully organized lesson histories",
      anyDevice: "Works on any mobile device",
      secureArchitecture: "Secure Google-backed architecture",
      features: "Features",
      support: "Support",
      enterApp: "Start",
      googleSync: "Google Calendar Sync",
      googleDrive: "Google Drive Assets",
      lifecycleTitle: "The Learning Lifecycle.",
      lifecycleSubtitle: "Built for the Frictionless Music Studio.",
      onboardingTitle: "Instant Onboarding",
      onboardingDesc: "Teachers invite via email. Account merging happens automatically—zero manual data entry for either side.",
      carryOverTitle: "Continuity Automated",
      carryOverDesc: "Notes and files follow the student. Unfinished goals move to the next session for true learning progress.",
      modularTitle: "Modular Discovery",
      modularDesc: "Students access modular libraries curated by their mentors. Scores, audio, and exercises in one place.",
      unpluggedTitle: "Music Studio, Unplugged.",
      efficiencySub: "Automatic file permission management",
      curriculumSub: "Modular curriculum for bulk-planning",
      financialTitle: "Financial Harmony",
      financialDesc: "Transparent credit tracking. No more awkward billing conversations—just focus on the performance.",
      growthTitle: "Progress Intelligence",
      growthDesc: "Students visualize their journey. Teachers monitor 'Weekly Velocity' and roster health with precision.",
      footerSupport: "Support",
      privacy: "Privacy",
      terms: "Terms",
      discoverTitle: "Discover your Perfect Mentor.",
      discoverDesc: "Browse professional educators by instrument, style, or rate. Access a dedicated learning portal that keeps your progress organized and your resources accessible.",
      livePortal: "Live Portal",
      connectingMentor: "Connecting with your mentor...",
      smartDiscovery: "Smart Discovery",
      smartDiscoveryDesc: "Filter by expertise and availability.",
      learningHub: "Learning Hub",
      learningHubDesc: "Homework and scores in one place.",
      progressTracker: "Progress Tracker",
      progressTrackerDesc: "Visualize your growth with every session.",
      secureVerified: "Secure & Verified",
      secureVerifiedDesc: "Verified professional profiles."
    },
    publicPresence: {
      title: "Public Presence",
      content: "Create a professional public profile that prospective students can browse on the SharpNote marketplace. Showcase your expertise and grow your studio."
    }
  },
  fr: {
    common: {
      save: "Enregistrer",
      cancel: "Annuler",
      delete: "Supprimer",
      update: "Mettre à jour",
      loading: "Chargement...",
      success: "Succès",
      error: "Erreur",
      confirm: "Confirmer",
      back: "Retour",
      edit: "Modifier",
      done: "Terminé",
      status: "Statut",
      type: "Type",
      date: "Date",
      time: "Heure",
      day: "Jour",
      week: "Semaine",
      month: "Mois",
      quarter: "Trimestre",
      today: "Aujourd'hui",
      duration: "Durée",
      notes: "Exercices",
      credits: "Crédits",
      lessons: "Cours",
      student: "Élève",
      teacher: "Professeur",
      scheduled: "Prévu",
      completed: "Terminé",
      canceled: "Annulé",
      pending: "En attente",
      due: "À faire",
      left: "Restant",
      add: "Ajouter",
      remove: "Retirer",
      primary: "Principal",
      failed: "Échec",
      active: "Actif",
      archived: "Archivé",
      dropped: "Parti",
      paymentDue: "Paiement dû",
      actions: "Actions",
      assignColor: "Assigner une couleur",
      filter: "Filtrer",
      sort: "Trier",
      newest: "Récent",
      oldest: "Ancien",
      page: "Page",
      anonymous: "Utilisateur Anonyme",
      expandAll: "Tout Développer",
      collapseAll: "Tout Réduire",
      copy: "Copier",
      copied: "Copié !",
      total: "Total",
      publish: "Publier",
      unpublish: "Dépublier",
      published: "Publié",
      draft: "Brouillon",
      location: "Localisation",
      remote: "Cours à distance",
      preview: "Aperçu",
      contact: "Contacter",
      book: "Réserver un cours",
      backToEditor: "Retour à l'éditeur",
      draftMode: "Mode Brouillon",
      liveProfile: "Profil Public",
      editProfile: "Modifier le Profil",
      draftPreview: "Aperçu Brouillon"
    },
    nav: {
      dashboard: "Tableau de bord",
      students: "Élèves",
      planner: "Planning",
      insights: "Analyses",
      profile: "Paramètres",
      support: "Support & Feedback",
      guide: "Guide d'utilisation",
      programs: "Programmes",
      signOut: "Déconnexion",
      studioHub: "Salle de Classe",
      switchLanguage: "Changer de Langue",
      toggleTheme: "Changer le Thème",
      openMenu: "Ouvrir le Menu",
      workflow: "Fonctionnement",
      pricing: "Tarifs",
      marketplace: "Trouver un Prof",
      publicProfile: "Profil Public",
      reviews: "Feedback"
    },
    reviews: {
      title: "Feedback des élèves",
      desc: "Suivez votre réputation et consultez les avis de vos élèves.",
      avgRating: "Note moyenne",
      totalReviews: "Nombre d'avis",
      noReviews: "Aucun feedback n'a été reçu pour le moment.",
      anonymousDesc: "Par souci de confidentialité, les photos sont masquées et seuls les prénoms sont affichés.",
      ratingLabel: "{count} étoiles",
      filterAll: "Toutes les notes",
      sortByNewest: "Le plus récent",
      sortByOldest: "Le plus ancien",
      sortByHighest: "Mieux noté",
      sortByLowest: "Moins bien noté",
      reputationShield: "Bouclier de Réputation",
      rateMentor: "Notez votre Mentor",
      shareExperience: "Partagez votre expérience d'apprentissage",
      reviewComment: "Commentaire de l'avis",
      reviewPlaceholder: "Dites aux autres ce que vous aimez chez ce professeur...",
      submitFeedback: "Envoyer l'avis",
      updateFeedback: "Mettre à jour l'avis",
      feedbackTitle: "Retours des élèves",
      feedbackEmpty: "Pas encore d'avis public. Soyez le premier élève à laisser un commentaire !"
    },
    marketplace: {
      title: "Mentors musicaux d'exception",
      subtitle: "Trouvez des professeurs qualifiés pour accélérer votre apprentissage.",
      filters: "Affiner la recherche",
      instrument: "Choisir un instrument",
      priceRange: "Fourchette de prix",
      remoteOnly: "Cours à distance uniquement",
      viewProfile: "Voir le Profil",
      noTeachers: "Aucun professeur ne correspond à vos critères.",
      startingFrom: "À partir de",
      perHour: "/ heure",
      teachingProfessionallyTitle: "Vous enseignez professionnellement ?",
      teachingProfessionallyDesc: "Publiez votre profil et développez votre studio avec SharpNote.",
      nearMe: "Près de moi",
      distance: "À moins de {distance} km",
      allDistances: "Toutes distances",
      locationError: "Impossible de récupérer votre position.",
      km: "km",
      resetLocation: "Réinitialiser position",
      allCategories: "Toutes les catégories",
      searchPlaceholder: "Rechercher par nom, expertise ou style..."
    },
    publicProfile: {
      title: "Profil Public",
      desc: "Personnalisez votre profil pour attirer de nouveaux élèves sur le réseau SharpNote.",
      basicInfo: "Infos de base",
      tagline: "Phrase d'accroche",
      taglinePlaceholder: "ex: Virtuose du Jazz avec 15 ans d'expérience",
      bio: "Biographie",
      bioPlaceholder: "Parlez au monde de votre style d'enseignement et de votre parcours...",
      curriculum: "Spécialités",
      instruments: "Instruments enseignés",
      media: "Galerie & Médias",
      galleryDesc: "Ajoutez jusqu'à 5 liens d'images vous montrant en cours ou en scène.",
      videoUrl: "ID Vidéo YouTube",
      pricing: "Tarifs & Forfaits",
      hourlyRate: "Tarif horaire",
      addPack: "Ajouter un forfait",
      packLabel: "Nom du forfait (ex: 10 Cours)",
      packPrice: "Prix total du forfait",
      visibility: "Visibilité du profil",
      publishDesc: "Rendez votre profil visible au public.",
      unpublishDesc: "Cachez votre profil pendant vos modifications.",
      mustSignIn: "Inscrivez-vous pour contacter ce professeur.",
      preciseLocation: "Position précise",
      preciseLocationDesc: "(Pour la recherche par distance)",
      useCurrentLocation: "Utiliser ma position actuelle",
      locationFound: "Coordonnées définies !",
      certifiedEducator: "Éducateur Certifié SharpNote",
      safeVerified: "Sécurisé & Vérifié",
      safeVerifiedDesc: "Les transactions sont gérées directement via le système prépayé SharpNote une fois connecté à une classe.",
      stageName: "Nom d'artiste",
      city: "Ville",
      country: "Pays",
      lessonPackages: "Forfaits de cours",
      optional: "Optionnel",
      savingsPercent: "Économie %",
      profileRequirements: "Les profils doivent avoir tous les champs obligatoires et une bio (50 chars min) pour être publiés.",
      publishChanges: "Publier les modifications"
    },
    tour: {
      next: "Suivant",
      prev: "Retour",
      finish: "C'est parti !",
      skip: "Passer",
      relaunch: "Lancer la visite guidée",
      relaunchDesc: "Nouveau sur SharpNote ? Laissez-nous vous montrer les bases de la plateforme.",
      steps: {
        welcome: {
          title: "Bienvenue sur SharpNote !",
          content: "Vous êtes sur votre centre de commandement professionnel. Faisons le tour des essentiels."
        },
        schedule: {
          title: "Cours à venir",
          content: "Vos prochaines sessions s'affichent ici. Rejoignez un appel Meet, vérifiez les devoirs ou ouvrez la fiche élève en un clic."
        },
        learners: {
          title: "Prochains élèves",
          content: "Cette liste dynamique affiche les élèves ayant un cours bientôt. Sans cours prévu, ils restent dans votre Répertoire complet."
        },
        students: {
          title: "Répertoire Élèves",
          content: "Gérez tout votre effectif ici. Invitez de nouveaux élèves avec un lien magique et suivez leurs crédits avec précision."
        },
        planner: {
          title: "Planning Intelligent",
          content: "Utilisez le Planning pour synchroniser Google Calendar, gérer vos dispos et déplacer vos cours librement."
        },
        curriculum: {
          title: "Bibliothèque de Programmes",
          content: "Organisez vos supports pédagogiques en groupes. Appliquez des dizaines de fichiers à un cours en un seul clic."
        },
        insights: {
          title: "Analyses Studio",
          content: "Surveillez la santé de votre studio. Suivez la vélocité des cours et vos revenus avec des graphiques haute fidélité."
        },
        reviews: {
          title: "Feedback des élèves",
          content: "Gérez et consultez les avis de vos élèves. Utilisez ces retours pour affiner votre pédagogie et mettre en avant votre réputation."
        },
        publicPresence: {
          title: "Profil Public",
          content: "Créez un profil public professionnel que les futurs élèves pourront consulter sur le réseau SharpNote. Mettez en avant votre expertise et développez votre studio."
        },
        settings: {
          title: "Paramètres Studio",
          content: "Finalisez votre installation en connectant votre compte Google pour la synchro Calendrier et la gestion Drive."
        }
      }
    },
    pricing: {
      title: "Tarifs simples et transparents",
      subtitle: "La plateforme d'orchestration conçue pour l'éducateur professionnel.",
      forTeachers: "Pour les Professeurs",
      forStudents: "Pour les Élèves",
      studentPricingTitle: "Gratuit à vie",
      studentPricingDesc: "SharpNote est 100% gratuit pour les élèves. Vous payez uniquement votre professeur directement pour vos cours.",
      studentPricingPoints: [
        "Accédez à votre historique à tout moment",
        "Consultez et téléchargez vos exercices",
        "Rejoignez vos cours en ligne en un clic",
        "Suivez vos crédits de cours en transparence"
      ],
      testPeriodTitle: "Période de Test Communautaire",
      testPeriodDesc: "SharpNote est actuellement en accès anticipé. Toutes les fonctionnalités, y compris les capacités Pro, sont actuellement gratuites pour tous les éducateurs inscrits pendant notre phase de test.",
      freeTier: "Gratuit",
      proTier: "Pro",
      perMonth: "/ mois",
      getStarted: "Commencer",
      upgradeToPro: "Passer à Pro",
      recommended: "Recommandé",
      footerTitle: "Tous les outils pour réussir",
      footerSubtitle: "Aucune complexité, juste de l'orchestration pure.",
      directPaymentsTitle: "Paiements Directs",
      directPaymentsDesc: "SharpNote est un outil de gestion de studio, pas un processeur de paiement. Les élèves paient leurs professeurs via leurs propres méthodes préférées (Espèces, Virement, Stripe, etc.). Les tarifs indiqués ci-dessus pour les professeurs couvrent le coût de l'orchestration et des intégrations de la plateforme.",
      features: {
        googleCalendar: "Sync. bidirectionnelle Google Calendar",
        googleDrive: "Gestion des ressources Google Drive",
        creditSystem: "Système de crédits prépayés",
        homework: "Devoirs automatisés et reports",
        curriculum: "Bibliothèque de programmes modulaire",
        studentPortal: "Portail d'apprentissage dédié aux élèves",
        meet: "Génération Google Meet en un clic",
        multiDevice: "Optimisé pour mobile et desktop",
        analytics: "Analyses de studio avancées",
        studentLimit: "Jusqu'à 5 élèves",
        unlimitedStudents: "Élèves illimités",
      },
      grid: {
        hub: { title: "Centre de Classe", desc: "Un poste de commande central pour votre studio, vos élèves et votre programme." },
        roster: { title: "Gestion d'Effectif", desc: "Intégrez facilement de nouveaux élèves et gérez les historiques." },
        automation: { title: "Automatisation", desc: "Les notes et fichiers sont automatiquement reportés entre vos cours." },
        curriculum: { title: "Cerveau Studio", desc: "Organisez vos supports en groupes modulaires pour une planification en masse." },
        financial: { title: "Harmonie Financière", desc: "Suivi transparent des crédits prépayés pour professeurs et élèves." },
        growth: { title: "Intelligence de Croissance", desc: "Surveillez la 'Vélocité Hebdomadaire' et les tendances d'effectif." },
        privacy: { title: "Confidentialité", desc: "Nous relions vos outils Google Workspace sans vendre vos données." },
        more: { title: "Et bien plus...", desc: "Intégration Google Meet, exports ICS, bibliothèques de ressources, etc." }
      }
    },
    privacy: {
      title: "Politique de Confidentialité",
      lastUpdated: "Dernière mise à jour : Février 2026",
      overview: {
        title: "1. Aperçu",
        content: "SharpNote est conçu comme un outil pour l'\"Éducateur Sans Friction\". Nous accordons la priorité à votre vie privée et à la souveraineté de vos données. Nous ne vendons pas vos données personnelles. Nous collectons uniquement les informations essentielles à l'orchestration de votre studio et à l'intégration avec les services Google de votre choix."
      },
      googleData: {
        title: "2. Utilisation des données Google",
        desc: "SharpNote s'intègre à Google Calendar et Google Drive pour synchroniser les cours et gérer les supports pédagogiques.",
        points: [
          "Google Calendar : Nous lisons et écrivons des événements pour synchroniser votre planning de cours. Nous accédons uniquement à l'agenda spécifique que vous autorisez.",
          "Google Drive : Nous lions des fichiers pour les devoirs des élèves. Nous accordons des permissions de 'Lecteur' aux élèves que vous invitez explicitement.",
          "Google Identity : Nous utilisons Google for une authentification sécurisée."
        ]
      },
      dataSecurity: {
        title: "3. Sécurité des données",
        content: "Vos données sont stockées de manière sécurisée via Firebase (Google Cloud Platform). La communication entre votre navigateur et nos serveurs est cryptée via SSL/TLS. Nous exploitons l'architecture de sécurité de Google pour l'authentification, garantissant que vos mots de passe ne sont jamais stockés directement sur nos serveurs."
      },
      studentTeacher: {
        title: "4. Relation Professeur & Élève",
        content: "Les informations partagées entre un professeur and un élève (notes, fichiers, historique) sont limitées à ces deux parties. Les professeurs peuvent gérer l'accès des élèves aux fichiers et archiver les données selon les besoins de gestion du studio."
      },
      footerLink: "Des questions sur vos données ? Contactez le Support"
    },
    terms: {
      title: "Conditions d'Utilisation",
      lastUpdated: "Dernière mise à jour : Février 2026",
      acceptance: {
        title: "1. Acceptation des Conditions",
        content: "En accédant à SharpNote ou en l'utilisant, vous acceptez d'être lié par ces conditions. SharpNote est une plateforme permettant aux professeurs de musique indépendants de gérer leur studio professionnel."
      },
      usage: {
        title: "2. Utilisation du Service",
        desc: "Vous êtes responsable du maintien de la confidentialité de votre account.",
        points: [
          "Les professeurs sont responsables du contenu partagé avec les élèves.",
          "Le système de crédits est un outil de gestion ; SharpNote ne traite pas directement les paiements.",
          "SharpNote n'est pas responsable des litiges entre professeurs et élèves concernant les paiements ou la prestation des cours."
        ]
      },
      liability: {
        title: "3. Limitation de Responsabilité",
        content: "SharpNote est fourni \"en l'état\". Nous ne sommes pas responsables des interruptions de service, des pertes de données provenant des services Google connectés, ou des erreurs administratives commises par les utilisateurs au sein du système."
      },
      changes: {
        title: "4. Modifications du Service",
        content: "Nous nous réservons le droit de modifier ou d'interrompre certaines parties du service au fur et à mesure que nous faisons évoluer la plateforme pour les éducateurs."
      }
    },
    guide: {
      title: "Manuel d'Orchestration Studio",
      desc: "Documentation complète pour maîtriser votre environnement d'enseignement professionnel sur SharpNote.",
      toc: "Table des Matières",
      proTip: "Conseil Pro",
      sections: {
        intro: {
          title: "La Philosophie SharpNote",
          content: "SharpNote est fondé sur le principe de l'Éducateur sans friction. Notre objectif est de réduire la charge mentale des tâches administratives — facturation, planification et gestion de fichiers — vous laissant 100 % concentré sur l'expérience pédagogique. Le système est conçu comme une 'Source de Vérité' qui relie vos outils personnels (Google Calendar, Drive) avec le portail d'apprentissage de vos élèves."
        },
        onboarding: {
          title: "Intégration & Invitations",
          content: "Pour ajouter un élève, allez dans le répertoire 'Élèves' et cliquez sur 'Inviter un élève'. Entrez son e-mail. Si l'e-mail est déjà enregistré, la connexion se fait instantanément. Sinon, il est marqué 'En attente'. Une fois que l'élève s'inscrit avec cet e-mail spécifique, SharpNote fusionne automatiquement l'invitation et récupère son historique de cours et de crédits.",
          tip: "Les élèves n'ont pas besoin d'un lien d'inscription séparé — dites-leur simplement d'utiliser l'e-mail avec lequel vous les avez invités."
        },
        credits: {
          title: "Système de Crédits & Facturation",
          content: "SharpNote utilise un modèle de 'Crédits' prépayés. Chaque cours coûte 1 crédit. Lorsque vous marquez un cours comme 'Terminé', le solde de l'élève est automatiquement décrémenté. Dans vos paramètres de profil, définissez des 'Packs de Crédits' (ex: forfait 5 cours). Lorsqu'un élève vous paie (via votre méthode de facturation externe), enregistrez l'achat sur sa fiche SharpNote pour mettre à jour son solde et suivre vos revenus.",
          tip: "Utilisez le filtre 'Paiement dû' dans le répertoire pour voir rapidement qui doit renouveler son forfait."
        },
        planner: {
          title: "Planning Intelligent",
          content: "Le Planner est une interface de synchronisation bidirectionnelle avec Google Calendar. Glissez les cours pour les reprogrammer ; les modifications sont poussées instantanément sur Google. Cliquez et glissez sur des créneaux vides pour créer de nouveaux cours. Utilisez la fonction 'Bloquer temps' pour protéger vos heures personnelles ou vos pauses sans affecter les crédits élèves.",
          tip: "Vérifiez la 'Vue des disponibilités' dans la fenêtre de planification pour détecter les chevauchements avec vos sessions existantes."
        },
        lessons: {
          title: "Le Flux de Travail",
          content: "Chaque cours est une 'Performance'. Vous pouvez ajouter des exercices et lier des fichiers directement depuis votre Google Drive. SharpNote génère automatiquement un lien Google Meet si activé. À la fin, utilisez la fonction 'Reporter' pour déplacer les exercices inachevés ou des fichiers spécifiques vers la prochaine session prévue, assurant la continuité de l'apprentissage.",
          tip: "Cliquez sur le bouton 'Rejoindre Meet' directement depuis votre Tableau de Bord pour lancer l'appel."
        },
        churn: {
          title: "Gérer le départ d'un élève",
          content: "Lorsqu'un élève quitte votre studio, ne le supprimez pas immédiatement. Utilisez l'action 'Marquer comme parti' sur sa fiche. Cela le retire de votre liste active et révoque son accès aux fichiers Google Drive, tout em préservant son historique de cours et les données de revenus pour vos statistiques.",
          tip: "Vous pouvez 'Restaurer' un élève à tout moment s'il décide de revenir, ce qui rétablit automatiquement les permissions Drive."
        },
        curriculum: {
          title: "Bibliothèque de Programmes",
          content: "La section Programme est votre cerveau pédagogique modulaire. Organisez pistes d'accompagnement, partitions et documents en groupes (ex: 'Jazz Débutant' ou 'Exercices Gammes'). Lors de la planification d'un cours, utilisez le bouton 'Appliquer Programme' pour importer en un clic tous les fichiers d'un groupe dans cette session.",
          tip: "Vous pouvez glisser-deposer les groupes et les fichiers pour réorganiser librement votre structure de programme."
        },
        stats: {
          title: "Données & Analyses",
          content: "Utilisez l'onglet 'Analyses' pour surveiller votre 'Vélocité Hebdomadaire' (cours moyens par semaine) et la croissance de votre effectif. Définissez des objectifs dans le menu 'Ajuster Objectifs'. Plongez dans les graphiques par année ou par mois pour identifier les tendances saisonnières de votre volume d'enseignement.",
          tip: "Les statistiques filtrées vous montrent les 'Taux d'Assiduité' spécifiques à chaque élève et l'efficacité d'utilisation des crédits."
        }
      }
    },
    programs: {
      title: "Bibliothèque de Programmes",
      desc: "Organisez vos supports pédagogiques en groupes modulaires pour une planification rapide des cours.",
      newGroup: "Nouveau Groupe",
      editGroup: "Modifier Groupe",
      deleteGroup: "Supprimer Groupe",
      groupName: "Nom du Groupe",
      groupDesc: "Description",
      addFile: "Ajouter Fichier",
      noGroups: "Aucun groupe de programme créé pour le moment.",
      selectGroup: "Choisir un Programme",
      bulkAdd: "Appliquer Programme",
      applyTooltip: "Importer tous les fichiers de ce programme dans le cours actuel.",
      moveToRoot: "Déplacer au niveau supérieur",
      confirmDeleteTitle: "Supprimer le groupe ?",
      confirmDeleteDesc: "Êtes-vous sûr de vouloir supprimer '{name}' ? Cela déplacera tous ses sous-groupes au niveau supérieur. Cette action est irréversible.",
      selection: "Sélection",
      assetsSelected: "{count} ressources sélectionnées",
      searchPlaceholder: "Rechercher des groupes et des fichiers...",
      quickAddTooltip: "Ajouter rapidement des ressources",
      addSubTooltip: "Ajouter un sous-groupe",
      invalidMove: "Action invalide : un groupe ne peut pas être déplacé dans un de ses descendants.",
      movingFile: "Déplacement du fichier",
      movingGroup: "Déplacement du groupe",
      assetsTitle: "Ressources du programme",
      noAssets: "Aucune ressource liée à ce groupe.",
      assetsCount: "{count} ressources",
    },
    app: {
      verifyEmail: "Vérifiez votre email",
      linkSent: "Lien de vérification envoyé à",
      linkResent: "Lien renvoyé !",
      resendLink: "Renvoyer l'email de vérification",
      verifiedAction: "J'ai vérifié mon email",
      accessPending: "Accès en attente",
      accessPendingDesc: "Votre compte est créé mais n'a pas encore été assigné à un studio.",
      teacherContact: "Professeurs : Contactez",
      studentContact: "Élèves : Demandez à votre professeur de vous inviter via votre email enregistré.",
      checkAccess: "Vérifier l'accès",
    },
    dashboard: {
      hi: "Salut",
      performanceFeed: "Vos cours à venir",
      remainingSessions: "Vous avez {count} cours de prévus",
      newPerformance: "Nouveau cours",
      upcomingSchedule: "Cours à venir",
      noSessions: "Aucun cours n'est prévu pour le moment.",
      noSessionsAvailable: "Aucun exercice disponible.",
      noConnections: "Aucune connexion active.",
      activeRoster: "Liste active",
      upcomingLearners: "Élèves à venir",
      upcomingMentors: "Vos mentors",
      studioMentors: "Vos profs",
      performanceNotes: "Exercices de cours",
      linkedResources: "Ressources liées",
      openPortal: "Ouvrir le portail de la classe",
      detailsPending: "Aucun exercice disponible.",
      joinMeet: "Rejoindre Google Meet",
    },
    students: {
      directory: "Répertoire des élèves",
      manageRoster: "Gérez votre liste d'élèves et vos invitations en attente.",
      inviteStudent: "Inviter un élève",
      searchPlaceholder: "Rechercher par nom ou email...",
      noStudents: "Aucun élève trouvé.",
      updatingRoster: "Mise à jour de la liste",
      inviteFailed: "Échec de l'invitation.",
      showArchived: "Afficher les archivés",
      showActive: "Afficher les actifs",
      cardView: "Vue Cartes",
      listView: "Vue Liste",
      sorting: {
        nameAZ: "Nom (A-Z)",
        nameZA: "Nom (Z-A)",
        creditsLow: "Crédits (Croissant)",
        creditsHigh: "Crédits (Décroissant)",
        dateNewest: "Inscrit (Récent)",
        dateOldest: "Inscrit (Ancien)",
      },
      filtering: {
        all: "Tous les soldes",
        outOfCredits: "Sans crédits",
        lowCredits: "Crédits bas (1-2)",
        healthyBalance: "Solde sain (3+)",
      },
      inviteModal: {
        title: "Inviter un élève",
        desc: "Entrez son email pour créer une connexion. S'il n'a pas encore de compte, il rejoindra votre classe automatiquement après son inscription.",
        emailLabel: "Adresse Email",
        emailPlaceholder: "virtuose@exemple.fr",
        submit: "Créer une invitation",
        successTitle: "Invitation Créée",
        successDesc: "L'élève sera automatiquement ajouté lors de sa première connexion.",
        duplicateError: "Cet élève déjà dans votre répertoire.",
        magicLinkTitle: "Lien Magique",
        magicLinkDesc: "Copiez et partagez ce lien avec votre élève pour l'aider à s'inscrire.",
      },
    },
    planner: {
      title: "Planning de la classe",
      desc: "Coordonnez votre emploi du temps et vos disponibilités.",
      export: "Exporter ICS",
      sync: "Synchroniser",
      syncAction: "Synchroniser Agenda",
      syncing: "Synchronisation...",
      newSession: "Nouveau Cours",
      blockTimeAction: "Bloquer Temps",
      tips: {
        title: "Astuces du Planning",
        desc: "Glissez-déposez les sessions pour les reprogrammer. Cliquez sur une session pour modifier l'heure, l'élève ou les exercices.",
      },
      legend: {
        lesson: "Cours",
        blocked: "Bloqué",
      },
      syncWarning: {
        notConfigured: "La synchronisation Google Calendar n'est pas configurée. Les sessions n'existeront que dans SharpNote.",
        expired: "La connexion Google Calendar a expiré. Re-authentifiez-vous pour reprendre la synchronisation.",
        configureAction: "Configurer",
        reconnectAction: "Reconnecter",
      },
      errors: {
        dbConnection: "Connexion à la base de données perdue.",
        syncError: "Erreur de synchronisation.",
      },
      engine: "Votre programme organisé !",
    },
    stats: {
      engine: "Les chiffres de votre classe !",
      title: "Analyses de la classe",
      desc: "Plongez dans votre croissance professionnelle et l'efficacité de vos cours.",
      activeRoster: "Élèves Actifs",
      churnedStudents: "Élèves Partis",
      completionRate: "Taux de complétion",
      creditsSold: "Crédits Vendus",
      historical: "Historique",
      healthy: "Sain",
      target: "Objectif: 95%",
      billed: "Facturé",
      volumeTitle: "Volume de Performance",
      volumeDesc: "Densité des cours groupée par intervalle sélectionné.",
      completedSessions: "Sessions Terminées",
      lifecycleTitle: "Cycle de vie des Crédits",
      lifecycleDesc: "Ratio Facturé vs Utilisé.",
      utilized: "Utilisé",
      remaining: "Restant",
      utility: "Utilité",
      totalPotential: "Potentiel Total",
      activeLiability: "Dette Active",
      units: "Unités",
      efficiency: "Efficacité Opérationnelle",
      weeklyVelocity: "Vélocité hebdomadaire",
      avgSessions: "Moyenne sessions / sem",
      retention: "Session Retention",
      vsNoShow: "Terminé vs Absent",
      benchmark: "Référence: 85%",
      totalChurn: "Total Churn",
      vsActive: "Partis vs Actifs",
      pipeline: "Moteur de Croissance",
      growthForecast: "Progression des Objectifs",
      required: "Élèves Restants",
      capacityTip: "Capacité studio recommandée selon vos objectifs.",
      tenure: "Engagement moy.",
      duration: "Durée moy.",
      suggestion: "Suggestion",
      suggestionText: "Le suivi montre que {count} élèves sont partis. Utilisez ces données pour affiner votre stratégie.",
      harmonizing: "Harmonisation des données...",
      goalsTitle: "Objectifs Professionnels",
      setGoals: "Ajuster Objectifs",
      targetRoster: "Objectif Élèves Actifs",
      targetVolume: "Objectif Cours Hebdo",
      progression: "Progression",
      avgSessionsPerStudent: "Cours / Élève",
      allStudents: "Tous les Élèves",
      selectStudent: "Filtrer par Élève",
      viewingStatsFor: "Statistiques pour",
      attendanceRate: "Taux d'assiduité",
      learningFrequency: "Fréquence d'apprentissage",
      studentPerformance: "Performance Élève",
      creditUtilization: "Utilisation des Crédits",
      momentum: "Dynamique Hebdomadaire",
      momentumTip: "Moyenne des cours par semaine pour cet élève.",
      utilizationTip: "Utilisation du pack de crédits actuel.",
      insightLowCredits: "{name} manque de crédits ({count} restants). Pensez à demander un renouvellement.",
      insightConsistent: "{name} a une dynamique d'apprentissage très régulière. Excellent progrès !",
      insightIrregular: "Le planning de {name} est irrégulier ces derniers temps. Un suivi peut être nécessaire.",
      insightPerfectAttendance: "Assiduité parfaite pour {name} ! Maintenez la motivation.",
      estimatedEarnings: "Revenus estimés",
      allTime: "Total",
      revenueBreakdown: "Détail des revenus",
      noPurchases: "Aucun achat enregistré.",
      totalEstimated: "Total estimé",
      annualVolume: "Volume annuel de cours",
      monthlyBreakdown: "{year} - Détail",
      clickYearTip: "Cliquez sur une année pour voir le détail mensuel.",
      showingMonthly: "Affichage des cours par mois.",
      totalLessonsBought: "Total cours achetés",
      creditsAvailable: "Crédits disponibles",
      performanceQuality: "Qualité pédagogique",
      lessonsLast30Days: "Cours (30 derniers jours)",
      engagementRate: "Engagement moyen",
      enrollmentStatus: "Statut d'inscription",
      noRecommendations: "Aucune recommandation urgente pour {name} pour le moment.",
      studentHistory: "Historique - {name}",
      studentYearly: "{name} - {year}",
      yearlyBreakdown: "{year} - Détail",
      noData: "AUCUNE DONNÉE",
      activateInsights: "Terminez votre premier cours pour activer ces analyses.",
      trackLiability: "Définissez des packs de crédits et enregistrez des achats pour suivre la dette du studio.",
      overallRating: "Note globale",
      noRatings: "Pas d'avis",
      reviewsCount: "avis",
      lessonsDone: "terminés",
    },
    scheduleModal: {
      performance: "Cours",
      blockTime: "Bloquer temps",
      editPerformance: "Modifier Cours",
      editBlock: "Modifier Blocage",
      scheduleSession: "Planifier un cours",
      blockAvailability: "Bloquer disponibilité",
      editDesc: "Modifiez les détails de votre cours existant.",
      scheduleDesc: "Réservez un créneau de cours dans votre calendrier.",
      blockDesc: "Marquez des heures comme indisponibles (vacances, pauses, admin).",
      syncExpired: "Sync expirée",
      syncExpiredDesc: "Session invalide. Déconnexion...",
      confirmRemoval: "Confirmer la suppression",
      removalDesc: "Cela supprimera définitivement l'entrée de SharpNote et de votre calendrier Google. Action irréversible.",
      deletePermanently: "Supprimer définitivement",
      keepSession: "Garder la session",
      assignedStudent: "Élève assigné",
      selectStudent: "Sélectionner un élève...",
      creditsRemaining: "{count} Crédits Restants",
      blockTitle: "Titre du blocage",
      blockPlaceholder: "ex: Pause déjeuner, Personnel...",
      dateTime: "Date & Heure",
      durationStrategy: "Durée",
      sessionNotes: "Exercices du cours",
      notesPlaceholder: "Ajouter un exercice ou objectif...",
      assetLibrary: "Bibliothèque de fichiers",
      linkDrive: "Lier un fichier Drive",
      confirmPerformance: "Confirmer le Cours",
      blockStudio: "Bloquer heures studio",
      updateSession: "Mettre à jour session",
      authRequired: "Connexion cloud requise. Vérifiez les permissions Drive.",
      deploymentFailed: "Échec du déploiement. Pont Studio instable.",
      addNote: "Ajouter un exercice",
      noStudentsWarning: "Vous devez ajouter un élève à votre liste avant de planifier un cours.",
      goToStudents: "Aller au répertoire des élèves",
      availabilityView: "Vue des disponibilités",
      availabilitySub: "Sélectionnez un créneau ou visualisez les conflits",
      conflictDetected: "Conflit d'horaire détecté",
      conflictWith: "Ce créneau chevauche : {name}",
      ignoreConflict: "Ignorer le conflit & Confirmer",
      calendarTips: "Utilisez les flèches pour naviguer. En vue Mois, cliquez sur un jour pour zoomer. Glissez-deposer pour choisir une heure précise.",
      conferencing: "Conférence",
      createMeet: "Créer un Google Meet",
    },
    studentDetail: {
      backToDashboard: "Retour au tableau de bord",
      gone: "Parti",
      enrollmentDate: "Date d'inscription",
      joinedOn: "Inscrit le {date}",
      studioBalance: "Crédits de cours",
      available: "Disponibles",
      left: "Restant",
      completed: "Terminés",
      sessionRecorded: "Cours enregistré !",
      rebookDesc: "Les crédits ont été mis à jour. Souhaitez-vous planifier la prochaine session avec ces exercices ?",
      rebookAction: "Planifier la suivante",
      upcomingPerformance: "Cours à venir",
      noUpcoming: "Aucun cours prévu pour le moment.",
      archivedHistory: "Historique",
      noHistory: "Aucun historique de cours disponible.",
      planInstructions: "Exercices du cours",
      noInstructions: "Aucun exercice",
      attachedResources: "Fichiers attachés",
      noAttachments: "Aucune pièce jointe",
      flagAsGone: "Marquer comme parti",
      restoreStudent: "Restaurer l'élève",
      paidTotal: "Bibliothèque partagée",
      sharedFiles: "Bibliothèque partagée",
      browseFiles: "Voir les fichiers",
      filesTitle: "Ressources partagées",
      filesDesc: "Recherchez et filtrez tous les documents partagés lors de vos cours.",
      searchFiles: "Rechercher un fichier...",
      noFilesFound: "Aucun fichier ne correspond à vos critères.",
      allTypes: "Tous les formats",
      purchasePack: "Acheter un pack",
      logPurchase: "Enregistrer l'achat d'un pack pour {name}.",
      noPacksConfigured: "Aucun pack configuré.",
      goToSettings: "Aller aux paramètres",
      confirmPurchase: "Confirmer l'achat",
      closeRecord: "Fermer le dossier",
      viewProfile: "Voir la fiche élève",
      creditHistory: "Historique d'Évolution des Crédits",
      creditHistoryDesc: "Suivez tous les changements du solde de crédits.",
      noCreditHistory: "Aucun historique trouvé pour cet élève.",
      changeAmount: "Variation",
      balance: "Solde",
      typeManual: "Mise à jour Manuelle",
      typePack: "Achat de Forfait",
      typeLesson: "Cours Terminé",
      typeSync: "Sync Agenda",
      modals: {
        cancelTitle: "Annuler le cours ?",
        cancelDesc: "Cela supprimera définitivement le cours du planning. Cette action est est irréversible.",
        flagGoneTitle: "Marquer comme parti ?",
        flagGoneDesc: "Cela retirera {name} de votre liste d'élèves actifs. Ses données historiques seront conservées.",
        restoreTitle: "Restaurer l'élève ?",
        restoreDesc: "Cela remettra {name} dans votre liste d'élèves actifs.",
        deleteTitle: "Suppression définitive ?",
        deleteDesc: "Vous êtes sur le point de supprimer définitivement {name} et toutes ses données. Irreversibile.",
        earlyTitle: "Terminer en avance ?",
        earlyDesc: "Ce cours est prévu pour le {date}. Le marquer comme terminé maintenant déduira 1 crédit. Confirmer ?",
        completeTitle: "Cours terminé !",
        completeDesc: "1 crédit a été décompté. {count} crédits restants pour cet élève.",
        forwardNotes: "Sélectionnez les exercices à reporter",
        forwardFiles: "Sélectionnez les fichiers à reporter",
        loggedTitle: "Cours Enregistré !",
        loggedDesc: "Les crédits ont été mis à jour. Souhaitez-vous planifier le prochain cours pour {name} ?",
        bookNext: "Planifier le prochain cours",
        doneForNow: "Terminé pour aujourd'hui",
        confirmCompleteTitle: "Terminer le cours ?",
        confirmCompleteDesc: "Voulez-vous marquer ce cours comme terminé ? 1 crédit sera déduit du solde.",
      }
    },
    profile: {
      title: "Paramètres du compte",
      desc: "Gérez votre profil professionnel et vos intégrations studio.",
      personalInfo: "Accès Professionnel",
      fullName: "Nom complet",
      firstName: "Prénom",
      lastName: "Nom",
      phoneNumber: "Numéro de téléphone",
      loginEmail: "Email de connexion",
      googleSyncTitle: "Connexion à Google Calendar",
      googleSyncDesc: "Sauvegardez automatiquement vos cours vers votre calendrier Google préféré.",
      googleImportTitle: "Activer l'importation",
      googleImportDesc: "Importez automatiquement les cours depuis Google Calendar via l'email des élèves.",
      connectionExpired: "Connexion expirée",
      expiredDesc: "Votre session Google a expiré. Reconnectez-vous pour continuer la synchronisation.",
      reconnect: "Reconnecter le compte Google",
      targetCalendar: "Calendrier cible",
      syncDisabled: "Sync désactivée",
      syncEnabled: "Calendrier Google connecté",
      secureConnectionTeacher: "Seuls vos élèves peuvent voir votre nom, email et numéro de téléphone.",
      secureConnectionStudent: "Seuls vos professeurs peuvent voir votre nom, email et numéro de téléphone.",
      updateProfile: "Sauvegarder",
      updateFailed: "Échec de la mise à jour",
      currency: "Devise",
      creditPacksTitle: "Packs de Crédits",
      creditPacksDesc: "Définissez des forfaits que vos élèves peuvent acheter en {currency}.",
      packName: "Nom du Pack",
      price: "Prix",
      noPacks: "Aucun pack configuré.",
      automationTitle: "Automatisation du Studio",
      automationDesc: "Configurez les comportements par défaut lors de la création de sessions.",
      defaultMeet: "Meet par défaut",
      defaultMeetDesc: "Ajouter automatiquement un lien Google Meet aux nouveaux cours.",
      notifications: "Notifications",
      notificationsDesc: "Alerter les élèves et professeurs avant le début du cours.",
      minutesBefore: "minutes avant",
    },
    support: {
      title: "Support & Feedback",
      desc: "Besoin d'aide ou une idée pour améliorer SharpNote ? Nous sommes à votre écoute.",
      reportIssue: "Signaler un problème",
      reportIssueDesc: "Vous avez trouvé un bug ou quelque chose ne fonctionne pas ? Dites-le nous.",
      makeSuggestion: "Faire une suggestion",
      makeSuggestionDesc: "Une idée de nouvelle fonctionnalité ? Nous serions ravis de l'entendre.",
      contactAdmin: "Contacter l'administrateur",
      emailUs: "Contacter le support"
    },
    login: {
      signIn: "Se connecter",
      register: "S'inscrire",
      email: "Email",
      password: "Mot de passe",
      googleSync: "Connexion avec Google",
      authFailed: "Échec de l'authentification. Vérifiez vos identifiants.",
      createAccount: "Créer un compte",
      authenticate: "Authentifier",
      socialIntegration: "Intégration Sociale",
      home: "Accueil",
      tagline: "Gestion Professionnelle des Élèves",
    },
    landing: {
      tagline: "La plateforme pour les professeurs et élèves de musique",
      heroTitle1: "L'enseignement musical,",
      heroTitle2: "évolué.",
      heroDesc: "Pour les Profs : Orchestrez votre studio professionnel. Pour les Élèves : Trouvez des mentors d'exception et suivez vos progrès sur un portail dédié.",
      heroSlogan: "Visez juste, sans bémol. La plateforme d'orchestration pour tous.",
      ctaStart: "Côté Professeur",
      ctaMarketplace: "Trouver un Prof",
      ctaWatch: "Voir le fonctionnement",
      featuresTitle: "Conçu pour la performance",
      featuresDesc: "Meticuleusement pensé pour combler le fossé entre l'enseignement d'excellence et l'apprentissage assidu.",
      trustTitle1: "Votre Studio,",
      trustTitle2: "Libéré.",
      efficiency: "Gain d'efficiency",
      latePayments: "Paiements en retard",
      readyToOrchestrate: "Prêt à commencer ?",
      readyDesc: "Rejoignez les professeurs et élèves qui s'éloignent de la friction pour revenir à la musique.",
      launch: "Lancer SharpNote",
      precisionScheduling: "Planning de précision",
      schedulingDesc: "Synchronisation bidirectionnelle Google Calendar. Ajustez vos créneaux et vos élèves sont notifiés instantanément.",
      inviteMagic: "Connectez-vous via lien magique",
      organizedHistory: "Historiques de cours organisés",
      anyDevice: "Fonctionne sur n'importe quel appareil",
      secureArchitecture: "Architecture sécurisée par Google",
      features: "Fonctionnalités",
      support: "Support",
      enterApp: "Entrer",
      googleSync: "Sync Google Calendar",
      googleDrive: "Ressources Google Drive",
      lifecycleTitle: "Le Cycle d'Apprentissage.",
      lifecycleSubtitle: "Conçu pour le Studio Musical Moderne.",
      onboardingTitle: "Intégration Instantanée",
      onboardingDesc: "Les professeurs invitent par email. La fusion des comptes est automatique — zéro saisie manuelle pour les deux parties.",
      carryOverTitle: "Continuité Automatisée",
      carryOverDesc: "Notes and fichiers suivent l'élève. Les objectifs inachevés passent à la session suivante pour un vrai progrès.",
      modularTitle: "Découverte Modulaire",
      modularDesc: "Les élèves accèdent à des bibliothèques modulaires créées par leurs mentors. Partitions, audio et exercices réunis.",
      unpluggedTitle: "Studio Musical, Libéré.",
      efficiencySub: "Gestion automatique des permissions de fichiers",
      curriculumSub: "Programme modulaire pour planification en masse",
      financialTitle: "Transparence Financière",
      financialDesc: "Suivi transparent des crédits. Plus de discussions gênantes sur la facturation — concentrez-vous sur la musique.",
      growthTitle: "Intelligence de Progrès",
      growthDesc: "Les élèves visualisent leur parcours. Les professeurs surveillent la 'Vélocité Hebdomadaire' et la santé du studio.",
      footerSupport: "Assistance",
      privacy: "Confidentialité",
      terms: "Conditions",
      discoverTitle: "Découvrez votre mentor idéal.",
      discoverDesc: "Parcourez les professeurs professionnels par instrument, style ou tarif. Accédez à un portail dédié qui garde vos ressources organisées.",
      livePortal: "Portail en direct",
      connectingMentor: "Connexion avec votre mentor...",
      smartDiscovery: "Découverte Intelligente",
      smartDiscoveryDesc: "Filtrez par expertise et disponibilité.",
      learningHub: "Hub d'Apprentissage",
      learningHubDesc: "Exercices et partitions au même endroit.",
      progressTracker: "Suivi de Progrès",
      progressTrackerDesc: "Visualisez votre évolution à chaque cours.",
      secureVerified: "Sûr & Vérifié",
      secureVerifiedDesc: "Profils professionnels vérifiés."
    },
    publicPresence: {
      title: "Profil Public",
      content: "Créez un profil public professionnel que les futurs élèves pourront consulter sur le réseau SharpNote. Mettez en avant votre expertise et développez votre studio."
    }
  }
};

const LanguageContext = createContext<{
  language: Language;
  setLanguage: (lang: Language) => void;
  t: any;
}>({
  language: 'en',
  setLanguage: () => {},
  t: {},
});

export const TranslationProvider: FC<{ children: ReactNode }> = ({ children }) => {
  const getInitialLanguage = (): Language => {
    const saved = localStorage.getItem('language') as Language;
    if (saved && (saved === 'en' || saved === 'fr')) return saved;
    return navigator.language.startsWith('fr') ? 'fr' : 'en';
  };

  const [language, setLanguageState] = useState<Language>(getInitialLanguage());

  const setLanguage = (lang: Language) => {
    setLanguageState(lang);
    localStorage.setItem('language', lang);
  };

  const t = translations[language];

  return React.createElement(LanguageContext.Provider, { value: { language, setLanguage, t } }, children);
};

export const useTranslation = () => {
  const context = useContext(LanguageContext);
  if (!context) {
    throw new Error('useTranslation must be used within a TranslationProvider');
  }
  return context;
};